import { useCallback, useMemo } from 'react';
import {
  redirectToLeadReviewAuto,
  redirectToLeadReviewHome,
  redirectToLeadFormAuto,
  redirectToLeadFormHome,
  redirectToAutoEquity,
  redirectToHomeRefi,
} from '@/utils/redirect';
import { useGetApplicationStatus } from '@/hooks/queries/useGetApplicationStatus';
import { ApplicationStatus } from '@/services/getApplicationStatus';
import { useGetAvailableLeadReview } from '@/hooks/queries/useGetAvailableLeadReview';
import {
  dispatchDiscoveryAutoEquityReviewEvent,
  dispatchDiscoveryAutoRefinEvent,
  dispatchDiscoveryHomeEquityReviewEvent,
  dispatchDiscoveryHomeEquityEvent,
} from '@/utils/analyticsEvents';

const isOpen = (status?: ApplicationStatus): boolean =>
  Boolean(status && status === ApplicationStatus.OPEN);

enum SpotlightStatus {
  ACTIVE = 'active',
  DEFAULT = 'default',
}

type UseDiscoverySectionResult = {
  isSuccess: boolean;
  handleClickAuto: () => void;
  handleClickHome: () => void;
  homeRefiStatus: SpotlightStatus | null;
  autoEquityStatus: SpotlightStatus | null;
};

const useDiscoverySection = (): UseDiscoverySectionResult => {
  const availableLeadReviewAuto = useGetAvailableLeadReview('auto');
  const availableLeadReviewHome = useGetAvailableLeadReview('home');
  const applicationStatus = useGetApplicationStatus();

  const statusAuto = useMemo(() => applicationStatus.data?.applications?.AUTO, [applicationStatus]);
  const statusHome = useMemo(() => applicationStatus.data?.applications?.HOME, [applicationStatus]);

  const canReviewAuto = useMemo(() => {
    let canReview = false;
    if (availableLeadReviewAuto.data) {
      const { reviewButton } = availableLeadReviewAuto.data;
      canReview = reviewButton;
    }

    return canReview;
  }, [availableLeadReviewAuto]);

  const canReviewHome = useMemo(
    () => availableLeadReviewHome.data?.reviewButton,
    [availableLeadReviewHome],
  );

  const handleClickAuto = useCallback((): void => {
    if (canReviewAuto) {
      dispatchDiscoveryAutoEquityReviewEvent();
      redirectToLeadReviewAuto();
    } else if (isOpen(statusAuto?.status)) {
      dispatchDiscoveryAutoRefinEvent();
      redirectToAutoEquity();
    } else {
      dispatchDiscoveryAutoRefinEvent();
      redirectToLeadFormAuto();
    }
  }, [statusAuto, canReviewAuto]);

  const handleClickHome = useCallback((): void => {
    if (canReviewHome) {
      dispatchDiscoveryHomeEquityReviewEvent();
      redirectToLeadReviewHome();
    } else if (isOpen(statusHome?.status)) {
      dispatchDiscoveryHomeEquityEvent();
      redirectToHomeRefi();
    } else {
      dispatchDiscoveryHomeEquityEvent();
      redirectToLeadFormHome();
    }
  }, [statusHome, canReviewHome]);

  const homeRefiStatus = useMemo(() => {
    const hasLeadOpened =
      applicationStatus?.data?.applications?.HOME?.status === ApplicationStatus.OPEN;

    if (hasLeadOpened) {
      return SpotlightStatus.ACTIVE;
    }
    return SpotlightStatus.DEFAULT;
  }, [applicationStatus]);

  const autoEquityStatus = useMemo(() => {
    const hasLeadOpened =
      applicationStatus?.data?.applications?.AUTO?.status === ApplicationStatus.OPEN;

    if (hasLeadOpened) {
      return SpotlightStatus.ACTIVE;
    }
    return SpotlightStatus.DEFAULT;
  }, [applicationStatus]);

  return {
    isSuccess: applicationStatus.isSuccess,
    handleClickAuto,
    handleClickHome,
    homeRefiStatus,
    autoEquityStatus,
  };
};

export { useDiscoverySection, SpotlightStatus };
